import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import UsersList from './UsersList';
import RolesList from './RolesList';
import apiService from '../../services/api';
import useToast from '../../hooks/useToast';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import './CommonStyles.css';

const Users = ({ activeTab: propActiveTab }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Tab State: 'users', 'roles', or 'agents'
  const [activeTab, setActiveTab] = useState(
    propActiveTab || location.state?.activeTab || 'users'
  );

  // Common Controls State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Users State
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [selectedViewUser, setSelectedViewUser] = useState(null);

  // Agents State
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState(null);

  // Agent Dropdown & View Modal & Add Modal State
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: null });
  const [selectedViewAgent, setSelectedViewAgent] = useState(null);

  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [savingAgent, setSavingAgent] = useState(false);
  const [agentForm, setAgentForm] = useState({
    surname: '',
    first_name: '',
    last_name: '',
    email: '',
    contact_no: '',
    address: '',
    cmmsn_percent: ''
  });

  // Filter States for User Management
  const [showFilters, setShowFilters] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAllowLogin, setFilterAllowLogin] = useState('all');
  const [roles, setRoles] = useState([]);

  // Fetch roles for filter dropdown on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await apiService.getRoles();
        let roleList = [];
        if (res && res.data) {
          roleList = Array.isArray(res.data) ? res.data : (res.data.data || []);
        } else if (Array.isArray(res)) {
          roleList = res;
        }
        setRoles(roleList);
      } catch (err) {
        /* ignore fallback */
      }
    };
    fetchRoles();
  }, []);

  // Synchronize state with prop / location state changes
  useEffect(() => {
    const targetTab = propActiveTab || location.state?.activeTab;
    if (targetTab) {
      setActiveTab(targetTab);
    }
    if (location.state?.openAddAgentModal) {
      setActiveTab('agents');
      setShowAddAgentModal(true);
    }
  }, [propActiveTab, location.state]);

  // Reset page and search when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
  }, [activeTab]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch Users data when activeTab is 'users'
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, debouncedSearchTerm]);

  // Fetch Agents data when activeTab is 'agents'
  useEffect(() => {
    if (activeTab === 'agents') {
      fetchAgents();
    }
  }, [activeTab, debouncedSearchTerm]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const userDetails = await apiService.getUserDetails();
      const bid = userDetails?.data?.business_id || userDetails?.business_id;

      const res = await apiService.getUsers({
        business_id: bid,
        search: debouncedSearchTerm
      });

      let userList = [];
      if (res && res.data) {
        userList = Array.isArray(res.data) ? res.data : (res.data.data || []);
      } else if (Array.isArray(res)) {
        userList = res;
      }
      setUsers(userList);
    } catch (err) {
      /* console.error('Error fetching users:', err); */
      setUsersError(err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      setAgentsError(null);
      const res = await apiService.getSalesCommissionAgents();
      let agentList = [];
      if (res && res.data) {
        agentList = Array.isArray(res.data) ? res.data : (res.data.data || []);
      } else if (Array.isArray(res)) {
        agentList = res;
      }
      setAgents(agentList);
    } catch (err) {
      /* console.error('Error fetching commission agents:', err); */
      setAgentsError(err.message || 'Failed to load sales commission agents');
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAdd = () => {
    if (activeTab === 'roles') {
      navigate('/roles/add');
    } else if (activeTab === 'agents') {
      setShowAddAgentModal(true);
    } else {
      navigate('/users/add');
    }
  };

  const handleExport = (format) => {
    let headers = [];
    let rows = [];
    let title = '';
    let filename = '';

    if (activeTab === 'users') {
      headers = ['Username', 'Full Name', 'Email', 'Role', 'Status'];
      rows = users.map(u => [
        u.username || u.email || '',
        u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.full_name || '',
        u.email || '',
        u.role?.name || u.role_name || '',
        u.status === 'active' || u.is_active ? 'Active' : 'Inactive'
      ]);
      title = 'Users List';
      filename = 'users_list';
    } else if (activeTab === 'agents') {
      headers = ['Name', 'Email', 'Contact No', 'Commission %', 'Address'];
      rows = agents.map(a => [
        a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.name || '',
        a.email || '',
        a.contact_no || a.mobile || '',
        a.cmmsn_percent != null ? `${a.cmmsn_percent}%` : '0%',
        a.address || ''
      ]);
      title = 'Sales Commission Agents';
      filename = 'sales_commission_agents';
    } else {
      headers = ['Role Name'];
      rows = [];
      title = 'Roles List';
      filename = 'roles_list';
    }

    if (!rows || rows.length === 0) {
      showError('No data to export');
      return;
    }

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
  };

  const handleAddAgentSubmit = async (e) => {
    e.preventDefault();
    if (!agentForm.first_name.trim()) {
      showError('First Name is required');
      return;
    }

    try {
      setSavingAgent(true);
      const payload = {
        surname: agentForm.surname,
        first_name: agentForm.first_name,
        last_name: agentForm.last_name,
        email: agentForm.email,
        contact_no: agentForm.contact_no,
        address: agentForm.address,
        cmmsn_percent: agentForm.cmmsn_percent ? parseFloat(agentForm.cmmsn_percent) : 0
      };

      const res = await apiService.createSalesCommissionAgent(payload);
      if (res && (res.success || res.status || res.id || res.data)) {
        showSuccess('Sales Commission Agent added successfully!');
        setShowAddAgentModal(false);
        setAgentForm({ surname: '', first_name: '', last_name: '', email: '', contact_no: '', address: '', cmmsn_percent: '' });
        setActiveTab('agents');
        fetchAgents();
      } else {
        showError(res?.message || 'Failed to add sales commission agent');
      }
    } catch (err) {
      /* console.error('Error adding sales commission agent:', err); */
      showError(err.message || 'Failed to add sales commission agent');
    } finally {
      setSavingAgent(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      const res = await apiService.deleteSalesCommissionAgent(agentId);
      if (res.success || res.status) {
        showSuccess('Agent deleted successfully');
        fetchAgents();
      } else {
        showError(res.message || 'Failed to delete agent');
      }
    } catch (err) {
      /* console.error('Error deleting agent:', err); */
      showError(err.message || 'Failed to delete agent');
    }
  };

  const toggleDropdown = (agentId, event) => {
    event.stopPropagation();

    if (openDropdownId === agentId) {
      setOpenDropdownId(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownHeight = 100;
    const viewportHeight = window.innerHeight;

    let top = null;
    let bottom = null;

    if (rect.bottom + dropdownHeight <= viewportHeight) {
      top = rect.bottom + window.scrollY;
    } else {
      bottom = viewportHeight - rect.top + window.scrollY;
    }

    setDropdownPosition({
      top,
      bottom,
      left: rect.left + window.scrollX
    });

    setOpenDropdownId(agentId);
  };

  // Filter Agents locally based on search
  const filteredAgents = agents.filter((agent) => {
    if (!debouncedSearchTerm) return true;
    const term = debouncedSearchTerm.toLowerCase();
    const fullName = `${agent.first_name || ''} ${agent.last_name || ''}`.toLowerCase();
    const email = (agent.email || '').toLowerCase();
    return fullName.includes(term) || email.includes(term);
  });

  const totalAgentItems = filteredAgents.length;
  const totalAgentPages = Math.ceil(totalAgentItems / itemsPerPage) || 1;
  const startAgentIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = filteredAgents.slice(startAgentIndex, startAgentIndex + itemsPerPage);

  const resetFilters = () => {
    setFilterRole('all');
    setFilterStatus('all');
    setFilterAllowLogin('all');
  };

  return (
    <div className="products-container">
      <div className="main-card">
        {/* Header Controls: Show Entries, Search, and Add Button */}
        <div className="table-controls" style={{ marginBottom: '1rem' }}>
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder={
                  activeTab === 'roles'
                    ? 'Search roles...'
                    : activeTab === 'agents'
                      ? 'Search agents...'
                      : 'Search users...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.55rem 0.85rem',
                  backgroundColor: (filterRole !== 'all' || filterStatus !== 'all' || filterAllowLogin !== 'all') ? '#4f46e5' : '#f1f5f9',
                  color: (filterRole !== 'all' || filterStatus !== 'all' || filterAllowLogin !== 'all') ? '#ffffff' : '#334155',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters {(filterRole !== 'all' || filterStatus !== 'all' || filterAllowLogin !== 'all') ? '•' : ''}
              </button>
            </div>

            {/* Export Dropdown */}
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { setShowExportDropdown(false); handleExport('CSV'); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { setShowExportDropdown(false); handleExport('Excel'); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { setShowExportDropdown(false); handleExport('Print'); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { setShowExportDropdown(false); handleExport('PDF'); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>

            <button className="control-btn-primary" onClick={handleAdd}>
              <span className="plus-icon">+</span>{' '}
              {activeTab === 'roles'
                ? 'Add Role'
                : activeTab === 'agents'
                  ? 'Add Agent'
                  : 'Add User'}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <UsersList
            users={users}
            loading={loadingUsers}
            error={usersError}
            onRefresh={fetchUsers}
            onView={(user) => setSelectedViewUser(user)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            filterRole={filterRole}
            filterStatus={filterStatus}
            filterAllowLogin={filterAllowLogin}
          />
        )}

        {activeTab === 'roles' && (
          <RolesList
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            searchTerm={debouncedSearchTerm}
          />
        )}

        {activeTab === 'agents' && (
          <div>
            {loadingAgents ? (
              <div className="loading-sales">
                <h3>⏳ Loading Commission Agents...</h3>
                <p>Fetching agents data from server...</p>
              </div>
            ) : agentsError ? (
              <div className="no-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>⚠️ Failed to Load Agents</h3>
                <p style={{ color: '#64748b', marginBottom: '1rem' }}>{agentsError}</p>
                <button className="control-btn-primary" onClick={fetchAgents}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th style={{ width: '110px' }}>Action</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Contact No</th>
                        <th>Commission %</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAgents.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                            No sales commission agents found. Create your first agent to get started.
                          </td>
                        </tr>
                      ) : (
                        paginatedAgents.map((agent) => (
                          <tr key={agent.id}>
                            <td className="actions-cell">
                              <div className="action-dropdown-container">
                                <button
                                  className="action-btn-trigger"
                                  onClick={(e) => toggleDropdown(agent.id, e)}
                                >
                                  Actions <span className="arrow-down">▼</span>
                                </button>
                                {openDropdownId === agent.id && ReactDOM.createPortal(
                                  <div
                                    className="action-dropdown-menu"
                                    style={{
                                      position: 'fixed',
                                      top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                      bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                      left: `${dropdownPosition.left}px`,
                                      zIndex: 1000
                                    }}
                                  >
                                    <button onClick={() => {
                                      setOpenDropdownId(null);
                                      setSelectedViewAgent(agent);
                                    }}>
                                      👁️ View
                                    </button>
                                    <button onClick={() => {
                                      setOpenDropdownId(null);
                                      handleDeleteAgent(agent.id);
                                    }}>
                                      🗑️ Delete
                                    </button>
                                  </div>,
                                  document.body
                                )}
                              </div>
                            </td>
                            <td>
                              {agent.first_name && agent.last_name
                                ? `${agent.first_name} ${agent.last_name}`
                                : agent.name || '-'}
                            </td>
                            <td>{agent.email || '-'}</td>
                            <td>{agent.contact_no || agent.mobile || '-'}</td>
                            <td>{agent.cmmsn_percent != null ? `${agent.cmmsn_percent}%` : '0%'}</td>
                            <td>{agent.address || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalAgentItems > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      marginTop: '0.5rem',
                      borderTop: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
                      Showing {startAgentIndex + 1} to {Math.min(currentPage * itemsPerPage, totalAgentItems)} of {totalAgentItems} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 0.5rem',
                          color: '#334155',
                          fontWeight: '500',
                          fontSize: '0.95rem'
                        }}
                      >
                        Page {currentPage} of {totalAgentPages}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage((p) => Math.min(totalAgentPages, p + 1))}
                        disabled={currentPage === totalAgentPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* View User Details Modal */}
      {selectedViewUser && (
        <div className="modal-overlay" onClick={() => setSelectedViewUser(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '580px', width: '92%', background: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>👁️ View User Details</h3>
              <button className="modal-close-btn" onClick={() => setSelectedViewUser(null)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem 1.5rem' }}>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Username</span>
                <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{selectedViewUser.username || selectedViewUser.email || '-'}</strong>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Full Name</span>
                <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>
                  {selectedViewUser.first_name || selectedViewUser.last_name
                    ? `${selectedViewUser.surname ? selectedViewUser.surname + ' ' : ''}${selectedViewUser.first_name || ''} ${selectedViewUser.last_name || ''}`.trim()
                    : selectedViewUser.full_name || '-'}
                </strong>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Email Address</span>
                <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>{selectedViewUser.email || '-'}</span>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Role</span>
                <span style={{ fontSize: '0.95rem', color: '#2563eb', fontWeight: '600' }}>{selectedViewUser.role?.name || selectedViewUser.role_name || '-'}</span>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Contact Number</span>
                <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>{selectedViewUser.contact_no || selectedViewUser.mobile || '-'}</span>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', display: 'block', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Status</span>
                {selectedViewUser.status === 'active' || selectedViewUser.is_active ? (
                  <span style={{ padding: '0.25rem 0.6rem', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>Active</span>
                ) : (
                  <span style={{ padding: '0.25rem 0.6rem', backgroundColor: '#fee2e8', color: '#991b1b', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>Inactive</span>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc' }}>
              <button type="button" onClick={() => setSelectedViewUser(null)} style={{ padding: '0.5rem 1.25rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Agent Modal */}
      {selectedViewAgent && (
        <div className="modal-overlay" onClick={() => setSelectedViewAgent(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>👁️ View Agent Details</h2>
              <button className="modal-close-btn" onClick={() => setSelectedViewAgent(null)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div><strong>Name:</strong> {selectedViewAgent.first_name && selectedViewAgent.last_name ? `${selectedViewAgent.first_name} ${selectedViewAgent.last_name}` : selectedViewAgent.name || '-'}</div>
              <div><strong>Email:</strong> {selectedViewAgent.email || '-'}</div>
              <div><strong>Contact No:</strong> {selectedViewAgent.contact_no || selectedViewAgent.mobile || '-'}</div>
              <div><strong>Commission %:</strong> {selectedViewAgent.cmmsn_percent != null ? `${selectedViewAgent.cmmsn_percent}%` : '0%'}</div>
              <div><strong>Address:</strong> {selectedViewAgent.address || '-'}</div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setSelectedViewAgent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Agent Popup Modal */}
      {showAddAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAddAgentModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', width: '92%', background: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>Add Sales Commission Agent</h3>
              <button className="modal-close-btn" onClick={() => setShowAddAgentModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleAddAgentSubmit}>
              <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem 1.5rem', width: '100%', boxSizing: 'border-box', overflow: 'visible', maxHeight: 'none' }}>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Prefix / Title:</label>
                  <select value={agentForm.surname} onChange={e => setAgentForm({ ...agentForm, surname: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}>
                    <option value="">Select Prefix</option>
                    <option value="Mr">Mr.</option>
                    <option value="Mrs">Mrs.</option>
                    <option value="Ms">Ms.</option>
                    <option value="Dr">Dr.</option>
                  </select>
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>First Name: <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" required placeholder="First Name" value={agentForm.first_name} onChange={e => setAgentForm({ ...agentForm, first_name: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Last Name:</label>
                  <input type="text" placeholder="Last Name" value={agentForm.last_name} onChange={e => setAgentForm({ ...agentForm, last_name: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Email Address:</label>
                  <input type="email" placeholder="agent@example.com" value={agentForm.email} onChange={e => setAgentForm({ ...agentForm, email: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Contact Number:</label>
                  <input type="text" placeholder="Contact Number" value={agentForm.contact_no} onChange={e => setAgentForm({ ...agentForm, contact_no: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Sales Commission Percentage (%):</label>
                  <input type="number" step="0.01" min="0" max="100" placeholder="e.g. 5.50" value={agentForm.cmmsn_percent} onChange={e => setAgentForm({ ...agentForm, cmmsn_percent: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }} />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Address:</label>
                  <textarea rows="3" placeholder="Agent Address" value={agentForm.address} onChange={e => setAgentForm({ ...agentForm, address: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: '#f8fafc' }}>
                <button type="button" onClick={() => setShowAddAgentModal(false)} style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={savingAgent} className="control-btn-primary" style={{ padding: '0.5rem 1.25rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}>
                  {savingAgent ? 'Saving...' : 'Save Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Filters Popup Modal */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', width: '92%', background: '#ffffff', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filter Users
              </h3>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>User Role:</label>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}
                >
                  <option value="all">All Roles</option>
                  {roles.map((r) => (
                    <option key={r.id || r.name} value={r.name || r}>
                      {r.name || r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>User Login Permission:</label>
                <select
                  value={filterAllowLogin}
                  onChange={(e) => setFilterAllowLogin(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}
                >
                  <option value="all">All Users</option>
                  <option value="1">Login Allowed</option>
                  <option value="0">Login Not Allowed</option>
                </select>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <button
                type="button"
                onClick={resetFilters}
                style={{ padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', color: '#64748b', cursor: 'pointer' }}
              >
                Reset Filters
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="control-btn-primary"
                style={{ padding: '0.5rem 1.25rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}
              >
                Apply & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
